import React, { useState } from 'react';
import { FormattedMessage } from '../../util/reactIntl';
import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { apiBaseUrl } from '../../util/api';
import { pathByRouteName } from '../../util/routes';
import { trackCreateAccount } from '../../util/gtmUtils';
import { pickUserFieldsData } from '../../util/userHelpers';
import { isSignupEmailTakenError } from '../../util/errors';
import LoginForm from '../AuthenticationPage/LoginForm/LoginForm';
import SignupForm from '../AuthenticationPage/SignupForm/SignupForm';
import {
  FacebookLogo,
  GoogleLogo,
} from '../AuthenticationPage/socialLoginLogos';
import { Heading, NamedLink, SocialLoginButton } from '../../components';
import css from './InlineAuthenticationForms.module.css';
import checkoutCss from './CheckoutPage.module.css';

const getNonUserFieldParams = (values, userFields) => {
  if (!values || typeof values !== 'object') return {};
  if (!userFields || typeof userFields !== 'object') return {};

  const userFieldNames = Object.keys(userFields);
  return Object.keys(values).reduce((picked, key) => {
    const isUserField = userFieldNames.includes(key);
    return !isUserField ? { ...picked, [key]: values[key] } : picked;
  }, {});
};

export const InlineAuthenticationForms = props => {
  const {
    showFacebookLogin,
    showGoogleLogin,
    submitLogin,
    loginError,
    idpAuthError,
    signupError,
    authInProgress,
    submitSignup,
    termsAndConditions,
    userTypes = [],
    userFields = {},
    userType = null,
    from,
  } = props;

  const [isLogin, setIsLogin] = useState(true);
  const routeConfiguration = useRouteConfiguration();
  const config = useConfiguration();
  const configUser = config?.user || {};
  const {
    userFields: configUserFields = {},
    userTypes: configUserTypes = [],
  } = configUser;

  // Use config values as fallback with defensive checks
  const finalUserFields =
    userFields && Object.keys(userFields).length > 0
      ? userFields
      : configUserFields;
  const finalUserTypes =
    userTypes && userTypes.length > 0 ? userTypes : configUserTypes;

  const getDataForSSORoutes = () => {
    const baseUrl = apiBaseUrl();

    // Default route where user is returned after successfull authentication
    const defaultReturn = pathByRouteName('LandingPage', routeConfiguration);

    // Route for confirming user data before creating a new user
    const defaultConfirm = pathByRouteName('ConfirmPage', routeConfiguration);

    const queryParams = new URLSearchParams({
      ...(defaultReturn ? { defaultReturn } : {}),
      ...(defaultConfirm ? { defaultConfirm } : {}),
      // Route where the user should be returned after authentication
      // This is used e.g. with EditListingPage and ListingPage
      ...(from ? { from } : {}),
      // The preselected userType needs to be saved over the visit to identity provider's service
      ...(userType ? { userType } : {}),
    });

    return { baseUrl, queryParams: queryParams.toString() };
  };

  const authWithFacebook = () => {
    const { baseUrl, queryParams } = getDataForSSORoutes();
    window.location.href = `${baseUrl}/api/auth/facebook?${queryParams}`;
  };

  const authWithGoogle = () => {
    const { baseUrl, queryParams } = getDataForSSORoutes();
    window.location.href = `${baseUrl}/api/auth/google?${queryParams}`;
  };

  // Handle signup submission with proper user field processing (matching AuthenticationPage)
  const handleSubmitSignup = values => {
    if (!values || typeof values !== 'object') {
      console.error('Invalid values provided to signup');
      return;
    }
    if (typeof submitSignup !== 'function') {
      console.error('submitSignup is not a function');
      return;
    }

    const {
      userType = null,
      email = '',
      password = '',
      fname = '',
      lname = '',
      displayName = '',
      ...rest
    } = values;

    if (!email || !password || !fname || !lname) {
      console.error('Required fields missing in signup form');
      return;
    }

    try {
      const displayNameMaybe = displayName
        ? { displayName: displayName.trim() }
        : {};

      const params = {
        email,
        password,
        firstName: fname.trim(),
        lastName: lname.trim(),
        ...displayNameMaybe,
        publicData: {
          userType,
          ...pickUserFieldsData(rest, 'public', userType, finalUserFields),
        },
        privateData: {
          ...pickUserFieldsData(rest, 'private', userType, finalUserFields),
        },
        protectedData: {
          ...pickUserFieldsData(rest, 'protected', userType, finalUserFields),
          ...getNonUserFieldParams(rest, finalUserFields),
        },
      };

      submitSignup(params);
      trackCreateAccount();
    } catch (error) {
      console.error('Error in handleSubmitSignup:', error);
    }
  };

  const showSocialLogins = showFacebookLogin || showGoogleLogin;

  // Error message handling (matching AuthenticationPage approach)
  const loginErrorMessage = (
    <div className={css.error}>
      <FormattedMessage id="AuthenticationPage.loginFailed" />
    </div>
  );

  const idpAuthErrorMessage = (
    <div className={css.error}>
      <FormattedMessage id="AuthenticationPage.idpAuthFailed" />
    </div>
  );

  const signupErrorMessage = (
    <div className={css.error}>
      {isSignupEmailTakenError(signupError) ? (
        <FormattedMessage id="AuthenticationPage.signupFailedEmailAlreadyTaken" />
      ) : (
        <FormattedMessage id="AuthenticationPage.signupFailed" />
      )}
    </div>
  );

  const loginOrSignupError =
    isLogin && !!idpAuthError
      ? idpAuthErrorMessage
      : isLogin && !!loginError
      ? loginErrorMessage
      : !!signupError
      ? signupErrorMessage
      : null;

  const fromMaybe = from ? { from } : null;
  const userTypeMaybe = userType ? { userType } : null;

  const tabNavigation = (
    <div className={checkoutCss.authTabs}>
      <button
        type="button"
        className={`${checkoutCss.authTab} ${
          isLogin ? checkoutCss.selected : ''
        }`}
        onClick={() => setIsLogin(true)}
      >
        <FormattedMessage id="AuthenticationPage.loginLinkText" />
      </button>
      <button
        type="button"
        className={`${checkoutCss.authTab} ${
          !isLogin ? checkoutCss.selected : ''
        }`}
        onClick={() => setIsLogin(false)}
      >
        <FormattedMessage id="AuthenticationPage.signupLinkText" />
      </button>
    </div>
  );

  return (
    <div className={css.content}>
      {tabNavigation}
      {loginOrSignupError}

      {isLogin ? (
        <LoginForm
          className={css.form}
          formId="InlineAuthenticationForms.login"
          onSubmit={submitLogin}
          inProgress={authInProgress}
        />
      ) : (
        <SignupForm
          className={css.form}
          formId="InlineAuthenticationForms.signup"
          onSubmit={handleSubmitSignup}
          inProgress={authInProgress}
          termsAndConditions={termsAndConditions}
          userTypes={finalUserTypes}
          userFields={finalUserFields}
        />
      )}

      {showSocialLogins && (
        <div className={css.idpButtons}>
          <div className={css.socialButtonsOr}>
            <span className={css.socialButtonsOrText}>
              <FormattedMessage id="AuthenticationPage.or" />
            </span>
          </div>

          {showFacebookLogin && (
            <div className={css.socialButtonWrapper}>
              <SocialLoginButton onClick={() => authWithFacebook()}>
                <span className={css.buttonIcon}>{FacebookLogo}</span>
                {isLogin ? (
                  <FormattedMessage id="AuthenticationPage.loginWithFacebook" />
                ) : (
                  <FormattedMessage id="AuthenticationPage.signupWithFacebook" />
                )}
              </SocialLoginButton>
            </div>
          )}

          {showGoogleLogin && (
            <div className={css.socialButtonWrapper}>
              <SocialLoginButton onClick={() => authWithGoogle()}>
                <span className={css.buttonIcon}>{GoogleLogo}</span>
                {isLogin ? (
                  <FormattedMessage id="AuthenticationPage.loginWithGoogle" />
                ) : (
                  <FormattedMessage id="AuthenticationPage.signupWithGoogle" />
                )}
              </SocialLoginButton>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
